/**
 * Filtering, search and A-Z sorting for the "Blog articles list" section.
 *
 * The cards are rendered by Liquid already in newest-first order, so this only
 * hides non-matching cards and reorders them when the A-Z pill is on. No
 * network requests, no markup templating.
 */

/**
 * @typedef {object} CardItem
 * @property {HTMLElement} el
 * @property {string} blockId - Section block that listed this article. A blog
 *   picked in several blocks yields one card set per block, and each pill
 *   narrows to its own set.
 * @property {string} title - Raw article title, used for A-Z sorting.
 * @property {string} haystack - Normalized title + tags + blog title.
 */

/**
 * Lowercases and strips diacritics so "Jalapeño" matches "jalapeno".
 * @param {string} value
 * @returns {string}
 */
function normalize(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Applies the blog pill, the submitted search term and the A-Z toggle.
 *
 * @param {Array<CardItem>} items - In document order, which is newest-first.
 * @param {{blockId?: string | null, term?: string, alphabetical?: boolean}} options
 * @returns {Array<CardItem>} Matching items, in the order they should appear.
 */
export function filterAndSort(items, { blockId = null, term = '', alphabetical = false } = {}) {
  const needle = normalize(term).trim();
  let result = items;

  if (blockId) {
    result = result.filter((item) => item.blockId === blockId);
  }

  if (needle) {
    result = result.filter((item) => item.haystack.includes(needle));
  }

  return alphabetical ? [...result].sort((a, b) => a.title.localeCompare(b.title)) : [...result];
}

class BlogArticlesList extends HTMLElement {
  #items = [];
  /** Block id of the active blog pill, or null. Several blocks can point at the
   *  same blog, so both the pill and its cards are keyed by block, not handle. */
  #activePill = null;
  #azActive = false;
  #searchTerm = '';

  connectedCallback() {
    this.grid = this.querySelector('[data-articles-grid]');
    this.emptyEl = this.querySelector('[data-articles-empty]');
    this.searchInput = this.querySelector('[data-articles-search]');
    this.form = this.querySelector('[data-articles-form]');

    this.#items = Array.from(this.querySelectorAll('[data-article-card]')).map((el) => ({
      el,
      blockId: el.dataset.blockId || '',
      title: el.dataset.title || '',
      haystack: normalize(el.dataset.search || ''),
    }));

    this.addEventListener('click', this.#onClick);
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  #onClick = (event) => {
    const pill = event.target.closest('[data-blog-pill], [data-az-pill]');
    if (!pill || !this.contains(pill)) return;

    if (pill.hasAttribute('data-az-pill')) {
      this.#azActive = !this.#azActive;
    } else {
      const blockId = pill.getAttribute('data-block-id');
      const clearing = pill.getAttribute('data-blog-pill') === '' || this.#activePill === blockId;
      this.#activePill = clearing ? null : blockId;
    }

    this.#syncPills();
    this.#apply();
  };

  #onSubmit = (event) => {
    event.preventDefault();
    this.#searchTerm = this.searchInput?.value ?? '';
    this.#apply();
  };

  #syncPills() {
    this.querySelectorAll('[data-az-pill]').forEach((pill) => {
      pill.classList.toggle('is-active', this.#azActive);
      pill.setAttribute('aria-pressed', String(this.#azActive));
    });

    this.querySelectorAll('[data-blog-pill]').forEach((pill) => {
      const blockId = pill.getAttribute('data-block-id');
      const active =
        pill.getAttribute('data-blog-pill') === ''
          ? this.#activePill === null
          : this.#activePill === blockId;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-pressed', String(active));
    });
  }

  #apply() {
    const visible = filterAndSort(this.#items, {
      blockId: this.#activePill,
      term: this.#searchTerm,
      alphabetical: this.#azActive,
    });
    const shown = new Set(visible.map((item) => item.el));

    for (const item of this.#items) {
      item.el.hidden = !shown.has(item.el);
    }

    // Reordering with `order` keeps the DOM untouched; Liquid already emitted
    // the cards newest-first, so the default state needs no order at all.
    for (const [index, item] of visible.entries()) {
      if (this.#azActive) {
        item.el.style.order = String(index);
      } else {
        item.el.style.removeProperty('order');
      }
    }

    if (this.emptyEl) this.emptyEl.hidden = visible.length > 0;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('blog-articles-list')) {
  customElements.define('blog-articles-list', BlogArticlesList);
}

export default BlogArticlesList;
